// Block D: one contact, in a SidePanel drawer from the right (full screen on a phone, by the kit).
//
// A QUICK SUMMARY (Carla, 2026-10-21), after mockups/contacts.html: the header (avatar, company, tags,
// "Open full profile"); Email / Book call / Invoice; the key details (Email, Also, Phone, Client
// portal, CRM, Added); the Lifetime value / Bookings / Unpaid tiles; tags as toggles; Log contact and
// Portal; the last five history entries then "View all history"; the last three linked notes then
// "View all notes" and "+ New note". NOTHING ELSE: meetings, invoices, proposals, contracts, the
// portal's details, questionnaires and intake answers are on the full contact page (/contacts/<id>).
//
// THE BUTTONS DO SOMETHING YOU CAN SEE (Carla, live, 2026-10-21):
//   Email     the pop-up's body BECOMES the composer (ContactComposer), with "← Back"; it had opened
//             below Tags and Log contact, out of view. Replacing the body, rather than scrolling to a
//             panel further down, is the one way that is in view on every device: the drawer is
//             full screen on a phone, where a scroll target below the fold is still below the fold
//             until the scroll lands, and the summary is not needed while she writes.
//   Book call the same composer, filled with her own booking link (/book/<bookingUsername>); it was a
//             mailto: link, which does nothing in the app or the iPhone app. No link yet: the
//             Meeting scheduler, where it is set up, with a toast saying why.
//   Invoice   the Invoices page, its form addressed to this contact (contactActions.newInvoiceFor).
import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showToast } from '../../utils/toast';
import { formatMoney } from '../../utils/currency';
import { SidePanel, Button, Avatar, StatusPill, StatTile, Chip, v } from '../../ui';
import ContactRecord from './ContactRecord';
import ContactComposer from './ContactComposer';
import LinkedNotes from '../Notes/LinkedNotes';   // decision 90 v2: the notes linked to this contact
import { PERSONAL_CATEGORIES, tagColor, primaryTag, orderedTags } from './contactTags';
import { navigateTo, ownBookingLink, ownSignOff, bookCallDraft, newInvoiceFor } from './contactActions';
import './Contacts.css';

export const fmtLong = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

// "£1,200" for whole amounts, "£1,200.50" otherwise; then "plus €300" for each other currency
// (decision 72: never added together).
export function moneyLabel(amount, currency, others = {}) {
  const whole = (n, c) => (Math.round(n * 100) % 100 === 0 ? formatMoney(n, c).replace(/\.00$/, '') : formatMoney(n, c));
  const extra = Object.entries(others || {}).filter(([, n]) => n).map(([c, n]) => whole(n, c));
  return [whole(amount || 0, currency), ...extra].join(' plus ');
}

// Book call, for the pop-up, the full page and the CRM card alike: the composer with her link, or the
// Meeting scheduler when there is no link (or no address to send it to). -> a seed, or null.
export async function bookCallSeed(contact, { onLeave } = {}) {
  let link = null;
  let failed = false;
  const signOff = ownSignOff();   // never throws
  try { link = await ownBookingLink(); } catch { failed = true; }
  if (link && contact.email) return bookCallDraft(contact, link, await signOff);
  if (failed) {
    showToast('Your booking link could not be read, so the Meeting scheduler is open instead.', { type: 'info' });
  } else {
    showToast(link ? 'This contact has no email address, so the Meeting scheduler is open instead.' : 'Set up your booking link in the Meeting scheduler first.', { type: 'info' });
  }
  onLeave?.();
  navigateTo('/scheduler');
  return null;
}

// The action buttons: one row, in the mockup's order and colours (Email the highlight, Book call
// outlined, Invoice the pale blue "create").
export function ContactActions({ contact, isPersonal, onEmail, onBookCall, onInvoice, booking }) {
  return (
    <div className={isPersonal ? 'contacts-actions contacts-actions--one' : 'contacts-actions'}>
      <Button variant="confirm" size="sm" disabled={!contact.email}
        title={contact.email ? undefined : 'This contact has no email address'}
        onClick={onEmail}>Email</Button>
      {!isPersonal && <Button variant="secondary" size="sm" onClick={onBookCall} disabled={booking}>{booking ? 'Opening…' : 'Book call'}</Button>}
      {!isPersonal && <Button variant="create" size="sm" onClick={onInvoice}>Invoice</Button>}
    </div>
  );
}

// The key details: the mockup's Email / Phone / Client portal rows, plus Also (decision 79), CRM and Added.
// onEmail: the address opens Aidey's composer (never mailto:, which does nothing in the app or the
// iPhone app; Carla, 2026-10-22).
export function ContactFacts({ contact, isPersonal, portalLabel, crm, onEmail }) {
  return (
    <dl className="contacts-facts">
      <div className="contacts-facts__row">
        <dt>Email</dt>
        <dd>{contact.email
          ? (onEmail ? <button type="button" className="contacts-linkbtn" onClick={onEmail}>{contact.email}</button> : <span>{contact.email}</span>)
          : <span className="contacts-muted">None</span>}</dd>
      </div>
      {/* Decision 79: a merged contact's other addresses; mail and bookings from them find this client. */}
      {Array.isArray(contact.emailAliases) && contact.emailAliases.length > 0 && (
        <div className="contacts-facts__row">
          <dt>Also</dt>
          <dd data-testid="contact-aliases">{contact.emailAliases.join(', ')}</dd>
        </div>
      )}
      <div className="contacts-facts__row">
        <dt>Phone</dt>
        <dd>{contact.phone ? <a className="contacts-link" href={`tel:${contact.phone.replace(/\s+/g, '')}`}>{contact.phone}</a> : <span className="contacts-muted">None</span>}</dd>
      </div>
      {!isPersonal && (
        <div className="contacts-facts__row">
          <dt>Client portal</dt>
          <dd>{portalLabel}</dd>
        </div>
      )}
      {!isPersonal && crm && (
        <div className="contacts-facts__row">
          <dt>CRM</dt>
          <dd>{crm}</dd>
        </div>
      )}
      <div className="contacts-facts__row">
        <dt>Added</dt>
        <dd>{fmtLong(contact.createdAt) || <span className="contacts-muted">Not recorded</span>}{contact.source && contact.source !== 'manual' ? ` · via ${contact.source}` : ''}</dd>
      </div>
    </dl>
  );
}

export function portalLabelOf(recordState) {
  const r = recordState.record;
  if (recordState.error) return 'Not loaded';
  if (!r) return 'Checking…';
  if (r.portal) return r.portal.isActive ? 'Active' : 'Inactive';
  return 'Not invited';
}

// The three tiles (from /record, read by the invoice rule on the server, so they never include an
// invoice this person may not open). A failed load says Unavailable, never £0.
export function ContactTiles({ recordState }) {
  const r = recordState.record;
  const money = r?.money;
  const tile = (value) => (recordState.error ? 'Unavailable' : !r ? '…' : value);
  return (
    <div className="contacts-tiles">
      <div className="contacts-tile" style={{ '--ui-bg': 'var(--pastel-periwinkle)' }}>
        <StatTile label="Lifetime value" value={tile(money ? moneyLabel(money.lifetime, money.currency, money.lifetimeOther) : 'Unavailable')} />
      </div>
      <div className="contacts-tile" style={{ '--ui-bg': 'var(--pastel-mint)' }}>
        <StatTile label="Bookings" value={tile(String(r?.meetings?.count ?? 0))} />
      </div>
      <div className="contacts-tile" style={{ '--ui-bg': 'var(--pastel-pink)' }}>
        <StatTile label="Unpaid" value={tile(money ? moneyLabel(money.unpaid, money.currency, money.unpaidOther) : 'Unavailable')} />
      </div>
    </div>
  );
}

// Turning the portal on, and sending its invite: the pop-up's Portal panel and the page's Portal tab.
export function usePortalEnable(contact) {
  const [portalStatus, setPortalStatus] = useState(null);   // null | 'loading' | 'invited' | 'error'
  const [portalError, setPortalError] = useState('');
  useEffect(() => { setPortalStatus(null); setPortalError(''); }, [contact.id]);
  async function enable() {
    setPortalStatus('loading');
    setPortalError('');
    try {
      const enableRes = await api(`/api/portal/enable/${contact.id}`, { method: 'POST' });
      const { portal } = await enableRes.json();
      if (contact.email) {
        try {
          await api(`/api/portal/${portal.id}/send-link`, { method: 'POST' });
        } catch (e) {
          throw new Error(`The portal is on, but the invite did not send: ${messageFromError(e, 'please try again.')}`);
        }
      }
      setPortalStatus('invited');
    } catch (err) {
      setPortalError(err.message || 'Something went wrong');
      setPortalStatus('error');
    }
  }
  return { portalStatus, portalError, enable };
}

export default function ContactDrawer({
  contact, isPersonal, tagColors, availableTags = [], canDelete,
  onClose, onEdit, onDelete, onMarkExisting, onToggleTag, crm, onOpenPage,
  startWith = null,   // 'email': open with the composer (the Contacts list's Email button)
}) {
  const [recordState, setRecordState] = useState({ record: null, error: null, loading: true });
  const [panel, setPanel] = useState(null);
  const startCompose = startWith === 'email' && contact.email ? { seed: null, title: 'New email' } : null;
  const [compose, setCompose] = useState(startCompose);   // null | { seed, title }: the body is the composer
  const [booking, setBooking] = useState(false);
  const { portalStatus, portalError, enable } = usePortalEnable(contact);

  useEffect(() => {
    setPanel(null);
    setCompose(startWith === 'email' && contact.email ? { seed: null, title: 'New email' } : null);
  }, [contact.id, contact.email, startWith]);

  async function bookCall() {
    setBooking(true);
    const seed = await bookCallSeed(contact, { onLeave: onClose });
    setBooking(false);
    if (seed) setCompose({ seed, title: 'Book a call' });
  }
  function newInvoice() {
    onClose();
    newInvoiceFor(contact.id);
  }
  const openPage = (tab) => { onClose(); onOpenPage?.(contact.id, tab); };

  const primary = primaryTag(contact, isPersonal);
  const tags = orderedTags(contact.tags);
  const toggleTags = isPersonal ? PERSONAL_CATEGORIES : [...new Set([...availableTags, ...tags])];

  return (
    <SidePanel
      open
      onClose={onClose}
      title={contact.name}
      color={tagColor(primary, tagColors)}
      actions={compose ? null : <>
        {contact.isNew && !isPersonal && <Button variant="tertiary" size="sm" onClick={onMarkExisting}>Mark as existing</Button>}
        <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
        {/* TEAM-1b: only owner and admin may delete a contact (decision 23); no Delete that would refuse. */}
        {canDelete && <Button variant="destructive" size="sm" onClick={onDelete}>Delete contact</Button>}
      </>}
    >
      {compose ? (
        <ContactComposer contact={contact} seed={compose.seed} title={compose.title}
          onBack={() => setCompose(null)}
          onSent={() => setCompose(null)} />
      ) : (
        <>
          <div className="contacts-hero" style={{ '--ui-bg': v(tagColor(primary, tagColors)) }}>
            <Avatar name={contact.name} size={64} color={tagColor(primary, tagColors)} />
            <div className="contacts-hero__text">
              <span className="contacts-hero__company">{contact.company || 'No company'}</span>
              <span className="contacts-pills">
                {tags.length > 0 ? tags.map((t) => <span key={t} className="contacts-hero__tag">{t}</span>) : <span className="contacts-hero__tag">{primary}</span>}
                {contact.clientPortal && <span className="contacts-portal-pill">Portal</span>}
                {contact.isNew && <StatusPill tone="pending">New</StatusPill>}
              </span>
              {onOpenPage && (
                <button type="button" className="contacts-hero__open" onClick={() => openPage()}>Open full profile <span aria-hidden="true">→</span></button>
              )}
            </div>
          </div>

          <ContactActions contact={contact} isPersonal={isPersonal} booking={booking}
            onEmail={() => setCompose({ seed: null, title: 'New email' })} onBookCall={bookCall} onInvoice={newInvoice} />

          <ContactFacts contact={contact} isPersonal={isPersonal} portalLabel={portalLabelOf(recordState)} crm={crm}
            onEmail={() => setCompose({ seed: null, title: 'New email' })} />

          {portalStatus === 'invited' && (
            <p className="contacts-ok" role="status">{contact.email ? `Portal activated and invite sent to ${contact.email}` : 'Portal activated'}</p>
          )}
          {portalStatus === 'error' && <p className="contacts-error" role="alert">{portalError}</p>}

          {!isPersonal && <ContactTiles recordState={recordState} />}

          <div className="contacts-block">
            <span className="contacts-eyebrow">{isPersonal ? 'Category' : 'Tags'}</span>
            <div className="contacts-chips">
              {toggleTags.map((t) => {
                const on = (contact.tags || []).includes(t);
                return (
                  <Chip key={t} selected={on} color={tagColor(t, tagColors)} onClick={() => onToggleTag(t)}
                    aria-label={`${t}${on ? ', on' : ', off'}`}>
                    <span aria-hidden="true">{on ? '✓' : '+'}</span> {t}
                  </Chip>
                );
              })}
            </div>
          </div>

          <ContactRecord
            contact={contact}
            onEnablePortal={enable}
            portalStatus={portalStatus}
            panel={panel}
            onPanelChange={setPanel}
            onState={setRecordState}
            historyLimit={5}
            onViewAll={onOpenPage ? () => openPage('activity') : undefined}
          />

          {/* Decision 90 v2: the newest three notes linked to this contact, each opening in the Notes
              editor; "View all notes" is the full page's Notes tab, "+ New note" starts one linked here. */}
          <div className="contacts-block">
            <LinkedNotes kind="contact" id={contact.id} title="Notes linked here" limit={3}
              onViewAll={onOpenPage ? () => openPage('notes') : null}
              emptyText={`No notes linked to ${contact.name || 'this contact'} yet.`} />
          </div>
        </>
      )}
    </SidePanel>
  );
}
